import { useState, type FormEvent } from 'react';
import { post, storeToken } from '../api';
import { useAuth } from '../auth';
import { ErrorBox, Spinner } from '../components/base';

export default function Account() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setDone(false);
    if (newPassword !== confirm) {
      setError('The new password and its confirmation do not match.');
      return;
    }
    setSaving(true);
    try {
      // The API ends every session that existed before the change - including
      // this one - and hands back a replacement, so the tab that did it stays
      // signed in while every other browser is signed out.
      const { token } = await post<{ token?: string }>('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (token) storeToken(token);
      setDone(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the password.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Account</h1>
        <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">
          Signed in as {user?.name} ({user?.email})
        </p>
      </div>

      <form onSubmit={submit} className="card space-y-4 p-6">
        <h2 className="font-semibold">Change password</h2>
        {error && <ErrorBox message={error} />}
        {done && (
          <p
            role="status"
            className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800
                       dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200"
          >
            Password changed. Any other browser signed in as you has been signed out.
          </p>
        )}

        <div>
          <label htmlFor="current" className="form-label">Current password</label>
          <input
            id="current" type="password" className="field" value={currentPassword} required
            autoComplete="current-password"
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="new" className="form-label">New password (minimum 12 characters)</label>
          <input
            id="new" type="password" className="field" value={newPassword} required minLength={12}
            autoComplete="new-password"
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="confirm" className="form-label">Confirm new password</label>
          <input
            id="confirm" type="password" className="field" value={confirm} required
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        <button type="submit" className="btn-primary" disabled={saving}>
          {saving && <Spinner className="h-4 w-4" />}
          Change password
        </button>
      </form>
    </div>
  );
}
